import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import API_URL from '../api';
import MessageStudentModal from './MessageStudentModal';
import { EmptyState, Modal, SkeletonCandidateCard, Stagger } from '../ui';
import PricingPlans from '../billing/PricingPlans';
import CheckoutModal from '../billing/CheckoutModal';
import { fetchPackages } from '../billing/checkout';
import '../billing/billing.css';
import './TalentPool.css';

// Shared results surface for BOTH talent-discovery entry points:
//   - "Recommended Students" tab on a job  -> <TalentPool jobId={...} />
//   - "Find Relevant Students" page        -> <TalentPool searchId={...} />
//
// PRODUCT RULE enforced here: no match score, percentage, rank or confidence
// value is ever rendered. The backend does not even send them. Match quality is
// conveyed only through the qualitative headline the API returns.

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

// The live progress checklist. Order matches TalentSearch.stage.
const STAGES = [
  { key: 'reading_jd', label: 'Reading job description' },
  { key: 'extracting_requirements', label: 'Extracting requirements' },
  { key: 'searching_database', label: 'Searching student database' },
  { key: 'finding_candidates', label: 'Finding relevant candidates' },
];

const POLL_INTERVAL_MS = 2000;

function friendlyError(err) {
  const status = err?.response?.status;
  const msg = err?.response?.data?.error || '';
  if (status === 422 || /too short|could not|unsupported|no text/i.test(msg)) {
    return msg || 'That job description could not be read. Try pasting the text instead.';
  }
  if (!err?.response || status >= 502) {
    return 'The matching service is unavailable right now. Please try again in a few minutes.';
  }
  return msg || 'Something went wrong finding students.';
}

function stageIndex(stage) {
  const found = STAGES.findIndex((s) => s.key === stage);
  return found === -1 ? STAGES.length : found;
}

// ── Progress checklist ──────────────────────────────────────────────────────
function ProgressChecklist({ stage, failed }) {
  const current = stageIndex(stage);
  return (
    <ul className="tp-progress" aria-live="polite">
      {STAGES.map((s, i) => {
        const done = stage === 'done' || i < current;
        const active = i === current && !failed;
        return (
          <li key={s.key} className={`tp-progress-row${done ? ' is-done' : ''}${active ? ' is-active' : ''}`}>
            <span className="tp-progress-icon" aria-hidden="true">
              {done ? '✓' : active ? <span className="tp-spin" /> : '○'}
            </span>
            <span>{s.label}</span>
          </li>
        );
      })}
    </ul>
  );
}

// ── Candidate card ──────────────────────────────────────────────────────────
function CandidateCard({ candidate, onUnlock, onView, onMessage, unlocking }) {
  const {
    name, college, department, year, bio, skills, matched_skills: matched,
    top_projects: projects, unlocked, resume_url: resumeUrl, email, phone,
    availability, notice_period: noticePeriod,
  } = candidate;

  const initial = (name || 'S').trim().charAt(0).toUpperCase();
  // Lead with the JD-relevant skills, then fill from the profile's own list.
  const shownSkills = useMemo(() => {
    const seen = new Set();
    const ordered = [];
    for (const skill of [...(matched || []), ...(skills || [])]) {
      const key = String(skill).toLowerCase();
      if (!skill || seen.has(key)) continue;
      seen.add(key);
      ordered.push({ name: skill, relevant: (matched || []).includes(skill) });
    }
    return ordered;
  }, [matched, skills]);

  return (
    <div className={`tp-card${unlocked ? ' tp-card--unlocked' : ''}`}>
      <div className="tp-card-head">
        <div className="tp-avatar" aria-hidden="true">{initial}</div>
        <div className="tp-card-id">
          <div className="tp-card-name">{name}</div>
          <div className="tp-card-sub">
            {[college, department, year ? `Year ${year}` : null].filter(Boolean).join(' · ')}
          </div>
        </div>
        {unlocked
          ? <span className="tp-badge tp-badge--unlocked">✓ Unlocked</span>
          : <span className="tp-badge tp-badge--locked" aria-label="Locked profile">🔒 Locked</span>}
      </div>

      {bio && <p className="tp-card-bio">{bio}</p>}

      {shownSkills.length > 0 && (
        <div className="tp-skills">
          {shownSkills.slice(0, 8).map((s) => (
            <span key={s.name} className={`tp-tag${s.relevant ? ' tp-tag--relevant' : ''}`}>
              {s.name}
            </span>
          ))}
          {shownSkills.length > 8 && <span className="tp-tag tp-tag--more">+{shownSkills.length - 8}</span>}
        </div>
      )}

      {projects?.length > 0 && (
        <div className="tp-projects">
          <div className="tp-section-label">Top projects</div>
          <ul>
            {projects.map((p, i) => (
              <li key={`${p.name}-${i}`}>
                <strong>{p.name}</strong>
                {p.description ? <span> — {p.description.slice(0, 110)}{p.description.length > 110 ? '…' : ''}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(availability?.length > 0 || noticePeriod) && (
        <div className="tp-meta-row">
          {availability?.length > 0 && <span>{availability.join(', ')}</span>}
          {noticePeriod && <span>Notice: {noticePeriod}</span>}
        </div>
      )}

      <div className="tp-card-foot">
        {unlocked ? (
          <>
            <div className="tp-contact">
              {email && <a href={`mailto:${email}`}>{email}</a>}
              {phone && <span>{phone}</span>}
            </div>
            <div className="tp-actions">
              {resumeUrl && (
                <a
                  className="tp-btn tp-btn-ghost"
                  href={`${API_URL}${resumeUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Resume
                </a>
              )}
              <button className="tp-btn tp-btn-ghost" onClick={() => onView(candidate)}>
                Full Profile
              </button>
              <button className="tp-btn tp-btn-primary" onClick={() => onMessage(candidate)}>
                Message
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="tp-locked-note">Resume and contact details unlock with your plan.</p>
            <button
              className="tp-btn tp-btn-primary"
              onClick={() => onUnlock(candidate)}
              disabled={unlocking}
            >
              {unlocking ? 'Unlocking…' : 'Unlock Profile'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Filter bar ──────────────────────────────────────────────────────────────
function FilterBar({ facets, filters, onChange, onReset, activeCount }) {
  const select = (key, label, options, formatter = (v) => v) => (
    <label className="tp-filter">
      <span>{label}</span>
      <select value={filters[key] || ''} onChange={(e) => onChange(key, e.target.value)}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{formatter(o.value)} ({o.count})</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="tp-filters">
      <label className="tp-filter tp-filter--grow">
        <span>Search</span>
        <input
          type="search"
          placeholder="Name, skill, college…"
          value={filters.q || ''}
          onChange={(e) => onChange('q', e.target.value)}
        />
      </label>
      {facets.colleges?.length > 0 && select('college', 'College', facets.colleges)}
      {facets.years?.length > 0 && select('year', 'Year', facets.years, (v) => `Year ${v}`)}
      <label className="tp-filter">
        <span>Skills</span>
        <input
          type="text"
          placeholder="e.g. React, Django"
          value={filters.skills || ''}
          onChange={(e) => onChange('skills', e.target.value)}
        />
      </label>
      {facets.availability?.length > 0 && select('availability', 'Availability', facets.availability)}
      {facets.work_modes?.length > 0 && select('work_mode', 'Work mode', facets.work_modes)}
      {activeCount > 0 && (
        <button className="tp-btn tp-btn-ghost tp-filter-reset" onClick={onReset}>
          Clear {activeCount} filter{activeCount > 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

// ── Upgrade modal ───────────────────────────────────────────────────────────
//
// Replaces a hardcoded price list that both disagreed with the one on the
// recruiter dashboard AND told the recruiter to "contact your account manager"
// — a dead end in the middle of the exact moment they wanted to pay. Packages
// now come from the server catalog and buying happens right here.
function UpgradeModal({ open, entitlements, packages, onClose, onSelect }) {
  if (!open) return null;

  const remaining = (entitlements?.credits_remaining ?? 0) + (entitlements?.free_remaining ?? 0);
  const subtitle = entitlements?.expired
    ? 'Your profile access has expired. Buy a package to unlock students again.'
    : remaining === 0
      ? 'You have used all your profile credits. Buy a package to keep unlocking students.'
      : `You have ${remaining} ${remaining === 1 ? 'unlock' : 'unlocks'} left.`;

  return (
    <Modal open={open} onClose={onClose} maxWidth={880} labelledBy="tp-upgrade-title">
      <div style={{ padding: '28px 26px 24px' }}>
        <h2 id="tp-upgrade-title" style={{ fontSize: 20, marginBottom: 6 }}>Unlock more students</h2>
        <p style={{ fontSize: 13.5, color: 'var(--text-muted)', marginBottom: 22 }}>
          {subtitle} Credits are added the moment your payment is confirmed, and your
          place in this list is kept.
        </p>
        {packages ? (
          <PricingPlans
            packages={packages}
            onSelect={onSelect}
            disabled={packages.payments_enabled === false}
            products={['profile']}
            compact
          />
        ) : (
          <div className="bl-plans">
            {[0, 1, 2].map((i) => <div key={i} className="ui-skel" style={{ height: 300, borderRadius: 16 }} />)}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export default function TalentPool({ jobId, searchId: searchIdProp, onViewProfile, embedded = false }) {
  const [search, setSearch] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [facets, setFacets] = useState({});
  const [entitlements, setEntitlements] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0, filtered: false });
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [error, setError] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [packages, setPackages] = useState(null);
  const [checkout, setCheckout] = useState({ open: false, pkg: null });
  const [messageTarget, setMessageTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const pollRef = useRef(null);

  // The catalog, so the paywall can open instantly when a recruiter runs out
  // mid-scroll rather than showing a spinner at the worst possible moment.
  useEffect(() => {
    fetchPackages().then(setPackages).catch(() => { /* modal falls back to a skeleton */ });
  }, []);
  const aliveRef = useRef(true);
  const searchId = search?.search_id || searchIdProp || null;

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => aliveRef.current && setToast(null), 3500);
  };

  // Kick off / adopt the search this pool is showing.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setCandidates([]);
    setSearch(null);

    const url = searchIdProp
      ? `${API_URL}/api/talent/search/${searchIdProp}`
      : `${API_URL}/api/talent/job/${jobId}`;

    axios.get(url, { headers: authHeaders() })
      .then((res) => { if (!cancelled) setSearch(res.data); })
      .catch((err) => { if (!cancelled) setError(friendlyError(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [jobId, searchIdProp]);

  // Poll while the sweep runs. The guard is hoisted into `isProcessing` so the
  // effect body never reads `search` — depending on the whole object would
  // re-arm the timer on every poll response.
  const isProcessing = search?.status === 'processing';

  useEffect(() => {
    if (!searchId || !isProcessing) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/talent/search/${searchId}`, { headers: authHeaders() });
        if (cancelled || !aliveRef.current) return;
        setSearch(res.data);
        if (res.data.status === 'processing') {
          pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (!cancelled) setError(friendlyError(err));
      }
    };

    pollRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [searchId, isProcessing]);

  // Load candidates once the sweep completes, and on filter/page changes.
  const fetchCandidates = useCallback(async (page = 1, activeFilters = filters) => {
    if (!searchId) return;
    setLoadingCandidates(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const res = await axios.get(
        `${API_URL}/api/talent/search/${searchId}/candidates?${params}`,
        { headers: authHeaders() }
      );
      if (!aliveRef.current) return;
      setCandidates(res.data.candidates || []);
      setFacets(res.data.facets || {});
      setEntitlements(res.data.entitlements || null);
      setPagination({
        page: res.data.page,
        total: res.data.total,
        total_pages: res.data.total_pages,
        filtered: res.data.filtered,
      });
      // Keep the headline in sync (count/message come from the search DTO).
      setSearch((prev) => (prev ? { ...prev, ...{
        total_candidates: res.data.total_candidates,
        message: res.data.message,
        pool_quality: res.data.pool_quality,
        match_basis: res.data.match_basis,
      } } : prev));
    } catch (err) {
      if (aliveRef.current) setError(friendlyError(err));
    } finally {
      if (aliveRef.current) setLoadingCandidates(false);
    }
  }, [searchId, filters]);

  useEffect(() => {
    if (search?.status === 'completed' && searchId) fetchCandidates(1, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search?.status, searchId]);

  // Debounce filter changes so typing doesn't fire a request per keystroke.
  const filterTimer = useRef(null);
  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => fetchCandidates(1, next), 350);
  };
  const handleFilterReset = () => {
    setFilters({});
    if (filterTimer.current) clearTimeout(filterTimer.current);
    fetchCandidates(1, {});
  };

  const handleUnlock = async (candidate) => {
    setUnlockingId(candidate.student_id);
    try {
      const res = await axios.post(
        `${API_URL}/api/talent/unlock`,
        {
          student_id: candidate.student_id,
          search_id: searchId,
          job_id: search?.job_id || undefined,
          source: search?.job_id ? 'talent_pool' : 'search',
        },
        { headers: authHeaders() }
      );
      setEntitlements(res.data.entitlements);
      // Re-fetch the current page so the unlocked card gets its real contact
      // details from the server rather than being patched client-side.
      await fetchCandidates(pagination.page, filters);
      showToast(res.data.already_unlocked
        ? `${candidate.name} was already unlocked.`
        : `${candidate.name} unlocked.`);
    } catch (err) {
      if (err?.response?.status === 402) {
        setEntitlements(err.response.data.entitlements || null);
        setUpgradeOpen(true);
      } else {
        showToast(friendlyError(err));
      }
    } finally {
      setUnlockingId(null);
    }
  };

  const handleView = (candidate) => {
    if (onViewProfile) onViewProfile(candidate);
    else window.open(`/student/${candidate.student_id}`, '_blank', 'noopener');
  };

  // Composing in place rather than navigating to /messages: leaving the page
  // would drop the pool, its filters and the current page of results.
  const handleMessage = (candidate) => {
    setMessageTarget({
      id: candidate.student_id,
      name: candidate.name,
      subtitle: [candidate.college, candidate.department].filter(Boolean).join(' · '),
    });
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const running = isProcessing;
  const failed = search?.status === 'failed';

  return (
    <div className={`tp-root${embedded ? ' tp-root--embedded' : ''}`}>
      {toast && <div className="tp-toast" role="status">{toast}</div>}

      {loading && <div className="tp-note"><span className="tp-spin" /> Loading talent pool…</div>}

      {!loading && error && !running && (
        <div className="tp-alert" role="alert">{error}</div>
      )}

      {running && (
        <div className="tp-running">
          <div className="tp-running-title">Finding students for you…</div>
          <ProgressChecklist stage={search.stage} failed={failed} />
        </div>
      )}

      {failed && (
        <div className="tp-alert" role="alert">
          We couldn’t finish this search{search.error ? `: ${search.error}` : ''}.
          {jobId && (
            <button
              className="tp-btn tp-btn-ghost"
              style={{ marginLeft: 10 }}
              onClick={() => {
                setSearch(null);
                setLoading(true);
                axios.post(`${API_URL}/api/talent/job/${jobId}/refresh`, null, { headers: authHeaders() })
                  .then((res) => setSearch(res.data))
                  .catch((err) => setError(friendlyError(err)))
                  .finally(() => setLoading(false));
              }}
            >
              Try again
            </button>
          )}
        </div>
      )}

      {search?.status === 'completed' && (
        <>
          <div className="tp-head">
            <div>
              {/* The ONLY place match quality is hinted at — qualitative, never numeric. */}
              <h3 className="tp-headline">{search.message}</h3>
              {search.match_basis?.length > 0 && (
                <p className="tp-basis">
                  Based on {search.match_basis.join(' · ')}
                </p>
              )}
            </div>
            <div className="tp-head-right">
              {entitlements && (
                <button
                  className="tp-entitlement"
                  onClick={() => setUpgradeOpen(true)}
                  title="View plans"
                >
                  {entitlements.expired
                    ? 'Access expired — buy credits'
                    : `${entitlements.remaining} of ${entitlements.limit} unlocks left`}
                </button>
              )}
              {jobId && (
                <button
                  className="tp-btn tp-btn-ghost"
                  onClick={() => {
                    axios.post(`${API_URL}/api/talent/job/${jobId}/refresh`, null, { headers: authHeaders() })
                      .then((res) => { setSearch(res.data); setCandidates([]); })
                      .catch((err) => showToast(friendlyError(err)));
                  }}
                >
                  Refresh
                </button>
              )}
            </div>
          </div>

          <FilterBar
            facets={facets}
            filters={filters}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
            activeCount={activeFilterCount}
          />

          {activeFilterCount > 0 && (
            <p className="tp-filtered-note">
              Showing {pagination.total} of {search.total_candidates} students in this pool.
            </p>
          )}

          {/* Skeletons hold the grid's shape so a filter change does not
              collapse the page and bounce it back. */}
          {loadingCandidates && (
            <div className="tp-grid">
              {[0, 1, 2, 3, 4, 5].map((i) => <SkeletonCandidateCard key={i} />)}
            </div>
          )}

          {!loadingCandidates && candidates.length === 0 && (
            activeFilterCount > 0
              ? <EmptyState
                  art="search"
                  title="No students match these filters"
                  body="Clear a filter to see the full pool again — the search itself found candidates, these filters just narrow it."
                  action={handleFilterReset}
                  actionLabel="Clear filters"
                />
              : <EmptyState
                  art="people"
                  title="No verified students yet"
                  body="New students are onboarded continuously. This pool refreshes as they are verified."
                />
          )}

          {!loadingCandidates && candidates.length > 0 && (
            <Stagger className="tp-grid" step={40}>
              {candidates.map((candidate) => (
                <CandidateCard
                  key={candidate.student_id}
                  candidate={candidate}
                  onUnlock={handleUnlock}
                  onView={handleView}
                  onMessage={handleMessage}
                  unlocking={unlockingId === candidate.student_id}
                />
              ))}
            </Stagger>
          )}

          {pagination.total_pages > 1 && (
            <div className="tp-pager">
              <button
                className="tp-btn tp-btn-ghost"
                disabled={pagination.page <= 1 || loadingCandidates}
                onClick={() => fetchCandidates(pagination.page - 1, filters)}
              >
                ← Previous
              </button>
              <span>Page {pagination.page} of {pagination.total_pages}</span>
              <button
                className="tp-btn tp-btn-ghost"
                disabled={pagination.page >= pagination.total_pages || loadingCandidates}
                onClick={() => fetchCandidates(pagination.page + 1, filters)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      <UpgradeModal
        open={upgradeOpen}
        entitlements={entitlements}
        packages={packages}
        onClose={() => setUpgradeOpen(false)}
        onSelect={(pkg) => { setUpgradeOpen(false); setCheckout({ open: true, pkg }); }}
      />

      <CheckoutModal
        open={checkout.open}
        pkg={checkout.pkg}
        onClose={() => setCheckout({ open: false, pkg: null })}
        onSuccess={(outcome) => {
          // Take the balance straight from the verification response, then
          // re-fetch the page so newly affordable cards show their real state.
          if (outcome?.entitlements) setEntitlements(outcome.entitlements);
          fetchCandidates(pagination.page, filters);
          showToast('Credits added — you can keep unlocking students.');
        }}
      />

      <MessageStudentModal
        open={!!messageTarget}
        student={messageTarget}
        onClose={() => setMessageTarget(null)}
        onSent={(s) => showToast(`Message sent to ${s.name}.`)}
      />
    </div>
  );
}
